// Lightweight equirectangular projection of GeoJSON into SVG path strings.
// No external mapping lib needed — constituency polygons are pre-simplified.

export interface GeoFeature {
  type: "Feature";
  properties: Record<string, any>;
  geometry: {
    type: "Polygon" | "MultiPolygon";
    coordinates: any;
  };
}

export interface GeoCollection {
  type: "FeatureCollection";
  features: GeoFeature[];
}

export interface Projected {
  code: string;
  name: string;
  props: Record<string, any>;
  path: string;
  cx: number;
  cy: number;
}

export interface ProjectResult {
  shapes: Projected[];
  width: number;
  height: number;
}

export function projectCollection(
  fc: GeoCollection,
  size = 1000,
  pad = 12,
): ProjectResult {
  // bounds
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  const eachCoord = (geom: GeoFeature["geometry"], cb: (x: number, y: number) => void) => {
    const polys = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
    polys.forEach((poly: number[][][]) =>
      poly.forEach((ring) => ring.forEach(([x, y]) => cb(x, y))),
    );
  };

  fc.features.forEach((f) =>
    eachCoord(f.geometry, (x, y) => {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }),
  );

  const w = maxX - minX || 1;
  const h = maxY - minY || 1;
  const scale = Math.min((size - pad * 2) / w, (size - pad * 2) / h);
  const drawW = w * scale + pad * 2;
  const drawH = h * scale + pad * 2;

  const px = (x: number) => pad + (x - minX) * scale;
  const py = (y: number) => pad + (maxY - y) * scale; // flip Y for screen coords

  const shapes: Projected[] = fc.features.map((f) => {
    const polys =
      f.geometry.type === "Polygon" ? [f.geometry.coordinates] : f.geometry.coordinates;
    let d = "";
    let sx = 0,
      sy = 0,
      n = 0;
    polys.forEach((poly: number[][][]) => {
      poly.forEach((ring) => {
        ring.forEach(([x, y], i) => {
          const X = px(x),
            Y = py(y);
          d += (i === 0 ? "M" : "L") + X.toFixed(1) + " " + Y.toFixed(1);
          sx += X;
          sy += Y;
          n++;
        });
        d += "Z";
      });
    });
    return {
      code: f.properties.code,
      name: f.properties.name,
      props: f.properties,
      path: d,
      cx: n ? sx / n : 0,
      cy: n ? sy / n : 0,
    };
  });

  return { shapes, width: drawW, height: drawH };
}
